import { SectionOneForm } from '@/features/section-1/application/validators/section-one.schema';
import Section2 from '@/features/section-2/application/domain/entities/Section2';
import type { PrintedDocumentRow } from '@/features/section-2/application/domain/entities/PrintedDocumentRow';
import {
  formatSignerNameLastFirst,
  isUndefinedOrNullOrEmptyString,
  normalizeAddressDisplay,
} from '@/shared/infra/utils/utils';
import {
  Supplement,
  SupplementCoreFields,
} from '@/features/supplement/infra/repositories/dto/res/SupplementResponseDto';
import SectionOne from '@/features/section-1/application/domain/entities/SectionOne';

/**
 * Whether the record actually used Supplement A (a preparer or translator assisted Section 1).
 *
 * VTK-1895 — the reconstructed I-9 rendered a Supplement A page for every record, so an empty page read
 * as a preparer section somebody began and abandoned rather than as "nobody assisted", and a two-page
 * record printed four pages.
 *
 * `interpreters` IS the Supplement A data: the Section 1 read returns `supplementAFields` as null unless
 * the employee declared they were assisted, and `Page3` already blanks every value on the page when the
 * array is empty — so the page this suppresses is provably contentless.
 */
export const hasSupplementAData = (section1: SectionOne | null | undefined): boolean =>
  (section1?.formFields?.interpreters?.length ?? 0) > 0;

/** Whether the record was reverified. The Section 1 read returns only SIGNED Supplement B rows. */
export const hasSupplementBData = (supplementB: readonly unknown[] | null | undefined): boolean =>
  (supplementB?.length ?? 0) > 0;

export const getRawSSN = (formattedSSN: string | undefined): string => {
  if (!formattedSSN) return '';
  return formattedSSN.replace(/-/g, '');
};

export interface Section2Page {
  firstDateOfEmployment: string;
  employerName: string;
  employerSignature: string;
  signatureDate: string;
  docAListKey: string;
  docBListKey: string;
  docCListKey: string;
  employerNameAndRole: string;
  accountName: string;
  locationAddress: string;
  A: ListProps | null;
  /** List A's Document 2 and 3 rows: a combination's other documents, or an extension notice. */
  A2: ListProps | null;
  A3: ListProps | null;
  B: ListProps | null;
  C: ListProps | null;
  /** The Additional Information box, one printed line each. */
  additionalInformationLines: string[];
}

export interface ListProps {
  /** The document's own title where it is one document of a combination; else the key's title. */
  title?: string;
  issueAuthority: string;
  docNum: string;
  expDate: string;
}

export const Section2ToSection2PageMapper = (
  section2: Section2 | undefined,
  section1: SectionOneForm
): Section2Page | null => {
  if (!section2) {
    return null;
  }
  const section2Form = section2.formFields;
  const isListAPresent = !isUndefinedOrNullOrEmptyString(section2Form.listASelection);
  const isListBPresent = !isUndefinedOrNullOrEmptyString(section2Form.listBSelection);
  const isListCPresent = !isUndefinedOrNullOrEmptyString(section2Form.listCSelection);
  return {
    firstDateOfEmployment: section2Form.firstWorkDay
      ? formatDateInput(section2Form.firstWorkDay)
      : '',
    employerName: section1.firstName + ' ' + section1.lastName,
    employerSignature: section2Form.signatureText || '',
    signatureDate: section2Form.signatureTimestamp
      ? formatDateInput(section2Form.signatureTimestamp)
      : '',
    docAListKey: section2Form.listASelection || '',
    docBListKey: section2Form.listBSelection || '',
    docCListKey: section2Form.listCSelection || '',
    // The persisted signerName is the display source — `signerFullName` is form state gated on
    // isAuthorisedUser, which leaves typed-signer records blank on the rendered form (VTK-1942).
    employerNameAndRole: withSignerTitle(
      formatSignerNameLastFirst(section2.signerName || section2Form.signerFullName),
      section2.signerTitle
    ),
    accountName: section2.businessName || '',
    ...listARows(
      isListAPresent,
      section2Form.documentMetadata?.listA,
      section2Form.listASelection,
      section2.printedListARows
    ),
    B: isListBPresent
      ? (servedListRow(section2Form.documentMetadata?.listB, section2.printedListBRows) ??
        getListProps(section2Form.documentMetadata?.listB, section2Form.listBSelection))
      : null,
    C: isListCPresent
      ? (servedListRow(section2Form.documentMetadata?.listC, section2.printedListCRows) ??
        listCProps(
          section2Form.documentMetadata?.listC,
          section2Form.listCSelection,
          section2Form.listCDocumentTypeCode
        ))
      : null,
    locationAddress: normalizeAddressDisplay(section2.businessAddress),
    additionalInformationLines: wrapAdditionalInformation(
      section2Form.additionalInformation,
      section2.additionalInformationLimits?.lineChars
    ),
  };
};

/**
 * The certification cell is "Last Name, First Name and Title", so a recorded title follows the name
 * as `Last, First, Title`, the way the corrected I-9 (v2) prints it. Skipped when the name already
 * carries it, either that way or as an older parenthesised "(Title)".
 */
export const withSignerTitle = (name: string, title: string | null | undefined): string => {
  const t = (title ?? '').trim();
  if (!t) return name;
  const lower = name.toLowerCase();
  const tl = t.toLowerCase();
  if (lower.endsWith(`, ${tl}`) || lower.includes(`(${tl})`)) return name;
  return name ? `${name}, ${t}` : t;
};

const text = (value: unknown): string =>
  typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
const dateOf = (value: unknown): string =>
  value instanceof Date || (typeof value === 'string' && value.trim() !== '')
    ? formatDateInput(value as string | Date)
    : '';

/**
 * Where the stored document has two numbers, both print in its Document Number cell (VTK-2038): the
 * card's own number beside the USCIS Number / A-Number that `getDocNum` puts first.
 */
const SECOND_NUMBER_KEY: Record<string, string> = {
  FORM_I551: 'i551_number',
  FORM_I766: 'i766_number',
  EMPLOYMENT_AUTHORIZATION_DOCUMENT: 'i766_number',
  FOREIGN_PASSPORT_WITH_I551_STAMP: 'alien_number',
};

/**
 * A Form I-94 receipt (an LPR's temporary I-551 stamp, a refugee's or a parolee's admission) is
 * numbered by the Form I-94 alone (M-274 4.4, 7.1); the A-Number only where no I-94 number was
 * recorded. The server serves these rows; this is the reading when it does not.
 */
const I94_NUMBER_FIRST = new Set(['FORM_I94_RECEIPT']);

const bothNumbers = (listData: DocumentMetadataBag, docTypeKey?: string | null): string => {
  if (docTypeKey && I94_NUMBER_FIRST.has(docTypeKey)) {
    return text(listData?.i94_number) || text(listData?.alien_number);
  }
  const first = getDocNum(listData, docTypeKey);
  const secondKey = docTypeKey ? SECOND_NUMBER_KEY[docTypeKey] : undefined;
  const second = secondKey ? text(listData?.[secondKey]) : '';
  return second && second !== first ? [first, second].filter(Boolean).join(' / ') : first;
};

/**
 * List A's rows. A combination prints one row per document, as the M-274 asks, and the server says
 * which: `printedListARows` on the Section 2 read names each row's title, issuer, number and date by
 * the stored key, and this only formats them. With no rows served it prints the one document row.
 */
const servedRow = (m: Record<string, unknown>, row: PrintedDocumentRow): ListProps => ({
  title: row.title,
  issueAuthority:
    'value' in row.issuingAuthority
      ? row.issuingAuthority.value
      : text(m[row.issuingAuthority.key]) || row.issuingAuthority.fallback,
  docNum:
    'documentKey' in row.number
      ? bothNumbers(m, row.number.documentKey)
      : row.number.keys
          .map((k) => text(m[k]))
          .filter(Boolean)
          .join(' / '),
  expDate: row.dateKey ? dateOf(m[row.dateKey]) : '',
});

/** List B's or C's row as the server serves it (a receipt), or null for the key's own reading. */
const servedListRow = (
  listData: DocumentMetadataBag,
  served: PrintedDocumentRow[] | null | undefined
): ListProps | null => (served?.length ? servedRow(listData ?? {}, served[0]) : null);

const listARows = (
  present: boolean,
  listData: DocumentMetadataBag,
  docTypeKey: string | null | undefined,
  served: PrintedDocumentRow[] | null | undefined
): Pick<Section2Page, 'A' | 'A2' | 'A3'> => {
  if (!present) return { A: null, A2: null, A3: null };
  if (!served?.length) return { A: getListProps(listData, docTypeKey), A2: null, A3: null };
  const m = listData ?? {};
  const [A, A2 = null, A3 = null] = served.map((row) => servedRow(m, row));
  return { A, A2, A3 };
};

/**
 * The printed box, a line at a time, at the size the server serves (`additionalInformationLimits` on
 * the Section 2 read). The server refuses a note the box could not print, and the signature step
 * refuses it before saving, so what is saved is what prints. With no size served the saved lines
 * print as they are.
 */
export const wrapAdditionalInformation = (
  value: string | null | undefined,
  lineChars: number = Number.POSITIVE_INFINITY
): string[] => {
  const lines: string[] = [];
  for (const paragraph of (value ?? '').split(/\r?\n/)) {
    let line = '';
    for (let word of paragraph.split(/\s+/).filter(Boolean)) {
      // A word longer than a line is broken, so nothing runs outside the box.
      while (word.length > lineChars) {
        if (line) lines.push(line);
        lines.push(word.slice(0, lineChars));
        word = word.slice(lineChars);
        line = '';
      }
      if (!word) continue;
      if (line && line.length + 1 + word.length > lineChars) {
        lines.push(line);
        line = word;
      } else {
        line = line ? `${line} ${word}` : word;
      }
    }
    if (line) lines.push(line);
  }
  return lines;
};

/**
 * Document-number keys in the order we would print them when the document type is unknown
 * (custom document types are configurable per account, so an unmapped type is normal).
 *
 * `alien_number` comes first because that is what the seeded rules do: every seeded type that carries
 * an A-Number field declares it at display order 1, required, ahead of that document's own card
 * number. The one type whose Document 1 value is genuinely not the A-Number is handled explicitly in
 * `PRIMARY_DOC_NUM_KEYS_BY_DOC_TYPE` below.
 */
const DOC_NUM_KEYS_BY_PRECEDENCE = [
  'alien_number',
  'us_passport_number',
  'foreign_passport_number',
  'i551_number',
  'i766_number',
  'i94_number',
  'document_bc_number',
  'receipt_number',
] as const;

/**
 * Which number prints in Document 1 for this Section 2 document type.
 *
 * The metadata bag legitimately holds several of `DOC_NUM_KEYS_BY_PRECEDENCE` at once — a Foreign
 * Passport + Form I-94 record carries `alien_number`, `i94_number` AND `foreign_passport_number`,
 * always, now that both numbers are required — so the number printed on the federal form must be
 * chosen by explicit precedence. It used to be whichever matching key came last in the object, and
 * `list_a_doc_metadata` is a MySQL `JSON` column, which normalises key order rather than preserving
 * insertion order: the value on the federal form was decided by storage-engine key ordering.
 *
 * Precedence is read off the seeded Section 2 field rules
 * (`verifyi9-BE .../seeders/*-seed-section2-doc-fields.js`). Where a type seeds `alien_number`
 * required at display order 1 with its own card number optional at order 2 — I-551, I-766, the List C
 * EAD, the I-94 receipt — the A-Number is the primary and is listed first. For a Permanent Resident
 * Card that is also independently the right federal-form value: E-Verify has the employer enter the
 * USCIS Number for a lawful permanent resident, and VTK-1985 §1.2A requires it. Keys not listed for a
 * type fall back to the order above.
 */
const PRIMARY_DOC_NUM_KEYS_BY_DOC_TYPE: Record<string, readonly string[]> = {
  US_PASSPORT: ['us_passport_number'],
  US_PASSPORT_CARD: ['us_passport_number'],
  FORM_I551: ['alien_number', 'i551_number'],
  FORM_I766: ['alien_number', 'i766_number'],
  EMPLOYMENT_AUTHORIZATION_DOCUMENT: ['alien_number', 'i766_number'],
  FORM_I94_RECEIPT: ['alien_number', 'i94_number'],
  // An MRIV's red number under "Expires On" is the document number (M-274 7.1, VTK-1985); a stamp
  // with no MRIV prints the passport's.
  FOREIGN_PASSPORT_WITH_I551_STAMP: ['visa_number', 'foreign_passport_number'],
  // The exception, and the reason this table exists: this pair is the only combination that needs TWO
  // List A slots — the passport in Document 1 and the Form I-94 in Document 2, per USCIS M-274 and
  // VTK-1940. Its seeded display order (alien_number 1, i94_number 2, foreign_passport_number 3)
  // describes the Section 2 entry form, not which number Document 1 carries. The second slot is
  // VTK-1962/VTK-1963's; until it lands, Document 1 shows the passport.
  FOREIGN_PASSPORT_WITH_FORM_I94: ['foreign_passport_number'],
  DRIVERS_LICENSE: ['document_bc_number'],
  GOVERNMENT_ID_CARD: ['document_bc_number'],
  CANADIAN_DRIVERS_LICENSE: ['document_bc_number'],
  RECEIPT_A: ['receipt_number'],
  RECEIPT_B: ['receipt_number'],
  RECEIPT_C: ['receipt_number'],
  // The card's own number is the SSN, which the matrix stores under its own key (VTK-2007; M-274 4.2,
  // Figure 4 prints it as the List C Document Number).
  SOCIAL_SECURITY_CARD: ['ssn_card_number'],
};

/** Only a non-blank value fills the slot: a typed-then-cleared field must not shadow a real number. */
const hasDocNumValue = (value: unknown): value is string | number =>
  typeof value === 'number' || (typeof value === 'string' && value.trim() !== '');

/** The persisted per-list metadata bag: seeded field keys, whose set varies by document type. */
type DocumentMetadataBag = Record<string, unknown> | null | undefined;

const getListProps = (listData: DocumentMetadataBag, docTypeKey?: string | null): ListProps => {
  if (!listData)
    return {
      issueAuthority: '',
      docNum: '',
      expDate: '',
    };
  return {
    // Section 2 stores the matrix's Issuing Authority as `issuing_authority` (VTK-2036); `iss_auth`
    // was never written by either repo and is read only as a fallback.
    issueAuthority: text(listData.issuing_authority) || text(listData.iss_auth),
    docNum: bothNumbers(listData, docTypeKey),
    expDate: getDocExpiry(listData, docTypeKey),
  };
};

/**
 * List C combinations made of a card and its Form I-797, printed by analogy to how the M-274 records
 * a Form I-797C in List C (5.x, the T and U nonimmigrant's): the receipt number as the document number
 * and the extended date as the expiration; 7.1 says nothing of how to record this one.
 * The expired conditional card with its Form I-797 for Form I-751 or I-829 is List C from matrix
 * version 7 (M-274 7.1, 13.3; VTK-1985); its own expired date must not print.
 */
const LIST_C_RECEIPT_COMBINATIONS = new Set(['FORM_I551_WITH_I797_EXTENSION']);

const listCProps = (
  listData: DocumentMetadataBag,
  docTypeKey?: string | null,
  documentTypeCode?: string | null
): ListProps => {
  const props = getListProps(listData, docTypeKey);
  if (!listData || !documentTypeCode || !LIST_C_RECEIPT_COMBINATIONS.has(documentTypeCode)) {
    return props;
  }
  const extended = text(listData.extension_expiration_date);
  return {
    ...props,
    docNum: text(listData.receipt_number) || props.docNum,
    expDate: extended ? formatDateInput(extended) : props.expDate,
  };
};

export const getDocNum = (listData: DocumentMetadataBag, docTypeKey?: string | null): string => {
  if (!listData) return '';
  const primaryKeys = (docTypeKey && PRIMARY_DOC_NUM_KEYS_BY_DOC_TYPE[docTypeKey]) || [];
  for (const key of [...primaryKeys, ...DOC_NUM_KEYS_BY_PRECEDENCE]) {
    const value = listData[key];
    if (hasDocNumValue(value)) return String(value).trim();
  }
  return '';
};

/**
 * The expiry that prints on the federal form, from whichever key the document type seeds it under.
 *
 * Every type but one seeds `expiration_date`; the three receipt types seed `receipt_expiration_date`
 * alongside `issued_date`, which is the date the receipt was issued and must never print here.
 */
const DOC_EXPIRY_KEYS = ['expiration_date', 'receipt_expiration_date'] as const;

/**
 * Documents that never expire, which print "N/A" as the M-274's own example does (4.2, Figure 4)
 * rather than a blank that reads as a missing date. Keyed on the type, never on an empty date: a
 * receipt or an expiring card with no date recorded must still print blank (VTK-2007). The form's
 * "Expiration Date (if any)" allows either, so this list only grows by deliberate choice.
 */
const NEVER_EXPIRES = new Set(['SOCIAL_SECURITY_CARD']);

export const getDocExpiry = (listData: DocumentMetadataBag, docTypeKey?: string | null): string => {
  if (!listData) return '';
  if (docTypeKey && NEVER_EXPIRES.has(docTypeKey)) return 'N/A';
  for (const key of DOC_EXPIRY_KEYS) {
    const value = listData[key];
    // Persisted metadata is JSON, so a date arrives as a string; `Date` is accepted because the
    // reader this replaced passed `string | Date` straight to `formatDateInput` and narrowing the
    // contract silently would be a change nobody asked for.
    if (value instanceof Date) return formatDateInput(value);
    if (typeof value === 'string' && value.trim() !== '') return formatDateInput(value.trim());
  }
  return '';
};

/**
 * What the Supplement B reverification row prints in Document Number and Expiration Date — VTK-2012.
 *
 * A reverification records ONE document, in List A or List C, so the populated bag is the one to
 * read. Both values come from the same readers the Section 2 page uses: the metadata keys are seeded
 * per document type, so the number cannot be taken from a fixed key — Form I-766, the ordinary
 * reverification document, stores `alien_number`/`i766_number` and printed blank while the page
 * looked only for `us_passport_number` and `document_bc_number`.
 */
export const getSupplementDocumentDisplay = (
  coreFields: SupplementDocumentFields | null | undefined
): { docNum: string; expDate: string } => {
  // The list is chosen by the SAME expression the Document Title cell prints, so the three cells
  // always describe one document. A record with no reverification document prints three blanks.
  const usedListA = !!coreFields?.listADocKey;
  const listData = usedListA ? coreFields?.listADocMetadata : coreFields?.listCDocMetadata;
  const docTypeKey = usedListA ? coreFields?.listADocKey : coreFields?.listCDocKey;
  return { docNum: getDocNum(listData, docTypeKey), expDate: getDocExpiry(listData, docTypeKey) };
};

/** The part of a supplement's core fields this reads — narrowed from the DTO so the two stay in step. */
type SupplementDocumentFields = Pick<
  SupplementCoreFields,
  'listADocKey' | 'listCDocKey' | 'listADocMetadata' | 'listCDocMetadata'
>;

export enum DocumentType {
  FRONT_DOCUMENT = 'FRONT_DOCUMENT',
  BACK_DOCUMENT = 'BACK_DOCUMENT',
}
export interface DocumentURLs {
  documentName: string;
  documentUrl: string;
  documentType: DocumentType;
}

/** A front/back pair for one document list entry. */
export interface DocumentGroup {
  listLabel: string;
  documentName: string;
  /** A labeled upload's matrix label (VTK-2039), shown as it is. */
  labeled?: boolean;
  front?: DocumentURLs;
  back?: DocumentURLs;
}

const DOCUMENT_FRONT_KEY = 'document_front_image';
const DOCUMENT_BACK_KEY = 'document_back_image';

/**
 * Extracts document front/back URL pairs from Section2, grouped by list (A, B, C).
 */
export const getDocumentGroupsFromSection2 = (section2: Section2 | null): DocumentGroup[] => {
  if (!section2) {
    return [];
  }
  const section2Form = section2.formFields;
  const documentMetadata = section2Form.documentMetadata;
  const groups: DocumentGroup[] = [];

  if (
    documentMetadata?.listA?.[DOCUMENT_FRONT_KEY] ||
    documentMetadata?.listA?.[DOCUMENT_BACK_KEY]
  ) {
    const group: DocumentGroup = {
      listLabel: 'List A',
      documentName: section2Form.listASelection || '',
    };
    if (documentMetadata.listA[DOCUMENT_FRONT_KEY]) {
      group.front = {
        documentName: `${section2Form.listASelection} (Front Side)`,
        documentUrl: documentMetadata.listA[DOCUMENT_FRONT_KEY],
        documentType: DocumentType.FRONT_DOCUMENT,
      };
    }
    if (documentMetadata.listA[DOCUMENT_BACK_KEY]) {
      group.back = {
        documentName: `${section2Form.listASelection} (Back Side)`,
        documentUrl: documentMetadata.listA[DOCUMENT_BACK_KEY],
        documentType: DocumentType.BACK_DOCUMENT,
      };
    }
    groups.push(group);
  }

  if (
    documentMetadata?.listB?.[DOCUMENT_FRONT_KEY] ||
    documentMetadata?.listB?.[DOCUMENT_BACK_KEY]
  ) {
    const group: DocumentGroup = {
      listLabel: 'List B',
      documentName: section2Form.listBSelection || '',
    };
    if (documentMetadata.listB[DOCUMENT_FRONT_KEY]) {
      group.front = {
        documentName: `${section2Form.listBSelection} (Front Side)`,
        documentUrl: documentMetadata.listB[DOCUMENT_FRONT_KEY],
        documentType: DocumentType.FRONT_DOCUMENT,
      };
    }
    if (documentMetadata.listB[DOCUMENT_BACK_KEY]) {
      group.back = {
        documentName: `${section2Form.listBSelection} (Back Side)`,
        documentUrl: documentMetadata.listB[DOCUMENT_BACK_KEY],
        documentType: DocumentType.BACK_DOCUMENT,
      };
    }
    groups.push(group);
  }

  if (
    documentMetadata?.listC?.[DOCUMENT_FRONT_KEY] ||
    documentMetadata?.listC?.[DOCUMENT_BACK_KEY]
  ) {
    const group: DocumentGroup = {
      listLabel: 'List C',
      documentName: section2Form.listCSelection || '',
    };
    if (documentMetadata.listC[DOCUMENT_FRONT_KEY]) {
      group.front = {
        documentName: `${section2Form.listCSelection} (Front Side)`,
        documentUrl: documentMetadata.listC[DOCUMENT_FRONT_KEY],
        documentType: DocumentType.FRONT_DOCUMENT,
      };
    }
    if (documentMetadata.listC[DOCUMENT_BACK_KEY]) {
      group.back = {
        documentName: `${section2Form.listCSelection} (Back Side)`,
        documentUrl: documentMetadata.listC[DOCUMENT_BACK_KEY],
        documentType: DocumentType.BACK_DOCUMENT,
      };
    }
    groups.push(group);
  }

  return groups;
};

/**
 * Extracts document front/back URL pairs from Supplement, grouped by list (A, C).
 */
export const getSupplementDocumentGroups = (supplement: Supplement): DocumentGroup[] => {
  const coreFields = supplement.coreFields;
  const groups: DocumentGroup[] = [];

  if (
    coreFields.listADocMetadata?.[DOCUMENT_FRONT_KEY] ||
    coreFields.listADocMetadata?.[DOCUMENT_BACK_KEY]
  ) {
    const group: DocumentGroup = {
      listLabel: 'List A',
      documentName: coreFields.listADocKey || '',
    };
    if (coreFields.listADocMetadata[DOCUMENT_FRONT_KEY]) {
      group.front = {
        documentName: `${coreFields.listADocKey} (Front Side)`,
        documentUrl: coreFields.listADocMetadata[DOCUMENT_FRONT_KEY] as string,
        documentType: DocumentType.FRONT_DOCUMENT,
      };
    }
    if (coreFields.listADocMetadata[DOCUMENT_BACK_KEY]) {
      group.back = {
        documentName: `${coreFields.listADocKey} (Back Side)`,
        documentUrl: coreFields.listADocMetadata[DOCUMENT_BACK_KEY] as string,
        documentType: DocumentType.BACK_DOCUMENT,
      };
    }
    groups.push(group);
  }

  if (
    coreFields.listCDocMetadata?.[DOCUMENT_FRONT_KEY] ||
    coreFields.listCDocMetadata?.[DOCUMENT_BACK_KEY]
  ) {
    const group: DocumentGroup = {
      listLabel: 'List C',
      documentName: coreFields.listCDocKey || '',
    };
    if (coreFields.listCDocMetadata[DOCUMENT_FRONT_KEY]) {
      group.front = {
        documentName: `${coreFields.listCDocKey} (Front Side)`,
        documentUrl: coreFields.listCDocMetadata[DOCUMENT_FRONT_KEY] as string,
        documentType: DocumentType.FRONT_DOCUMENT,
      };
    }
    if (coreFields.listCDocMetadata[DOCUMENT_BACK_KEY]) {
      group.back = {
        documentName: `${coreFields.listCDocKey} (Back Side)`,
        documentUrl: coreFields.listCDocMetadata[DOCUMENT_BACK_KEY] as string,
        documentType: DocumentType.BACK_DOCUMENT,
      };
    }
    groups.push(group);
  }

  return groups;
};

// Calendar dates (date of birth, first day of employment) arrive already formatted as MM/DD/YYYY.
// `new Date('04/16/2001')` parses a zone-less string as LOCAL midnight, which the UTC formatter below
// then rolls back a day for any viewer east of Greenwich, so a Scotland user read a DOB one day
// earlier than a Houston user off the same record. Format those lexically; only genuine instants
// (signature timestamps) may take the Date path.
export const formatDateInput = (value: string | Date): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
    if (mdy) {
      const [, month, day, year] = mdy;
      return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (ymd) {
      const [, year, month, day] = ymd;
      return `${month}/${day}/${year}`;
    }
  }
  try {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    });
  } catch {
    return '';
  }
};
